import { FlagBanner } from "@phosphor-icons/react";
import { canGroupBy } from "@plannello/core/fields";
import { m } from "@plannello/i18n/messages";
import { type ItemLite, type ItemPatch, STATUS_CATEGORIES } from "@plannello/shared";
import type { ReactNode } from "react";
import { PRIORITY_VALUES, PriorityIcon, priorityLabel } from "@/components/domain/PriorityIcon";
import { StatusIcon } from "@/components/domain/StatusIcon";
import { TypeIcon } from "@/components/domain/TypeIcon";
import { compareMilestones, milestoneLabel } from "@/components/planning/milestoneOrder";
import { Avatar } from "@/components/ui";
import type { Catalog } from "@/data/catalog";
import { labelSwatch, loc, statusAccent } from "@/lib/format";

/**
 * Grouping for lists (group headers) and boards (columns). Every group knows how to move an item
 * into itself (`patchFor`), which is what board drops and "Move to" use.
 */
export type GroupBy =
  | "status"
  | "priority"
  | "assignee"
  | "type"
  | "milestone"
  | "product"
  | "none"
  | `field:${string}`;

export type Group = {
  key: string;
  label: string;
  icon?: ReactNode;
  /** CSS colour of a 2px accent (board column top border), e.g. the custom colour of a status. */
  accent?: string;
  items: ItemLite[];
  /** Patch that moves `item` into this group; null when it already is (or cannot be moved). */
  patchFor: (item: ItemLite) => ItemPatch | null;
  /** Sub-groups (list "Sub-group by"); `items` still holds every item of the group. */
  children?: Group[];
};

export const BUILTIN_GROUPS: GroupBy[] = ["status", "priority", "assignee", "type", "milestone"];

/** Groupings a view may offer: the built-ins plus every single-select Field. */
export function groupingOptions(catalog: Catalog, opts: { product?: boolean } = {}) {
  const fields = catalog.labelGroups.filter((g) => canGroupBy(g));
  return [
    ...BUILTIN_GROUPS.map((g) => ({ id: g as GroupBy, label: groupLabel(g, catalog) })),
    ...(opts.product ? [{ id: "product" as GroupBy, label: groupLabel("product", catalog) }] : []),
    ...fields.map((f) => ({ id: `field:${f.slug}` as GroupBy, label: loc(f.name) })),
  ];
}

export function groupLabel(g: GroupBy, catalog: Catalog): string {
  switch (g) {
    case "status":
      return m.group_status();
    case "priority":
      return m.group_priority();
    case "assignee":
      return m.group_assignee();
    case "type":
      return m.group_type();
    case "milestone":
      return m.prop_milestone();
    case "product":
      return m.group_product();
    case "none":
      return m.group_none();
    default: {
      const slug = g.slice(6);
      const group = catalog.labelGroups.find((x) => x.slug === slug);
      return group ? loc(group.name) : slug;
    }
  }
}

/** Parses the `?group=` param; unknown or multi-select fields fall back to `fallback`. */
export function parseGroupBy(raw: string | undefined, catalog: Catalog, fallback: GroupBy): GroupBy {
  if (!raw) return fallback;
  if (["status", "priority", "assignee", "type", "milestone", "product", "none"].includes(raw)) return raw as GroupBy;
  const slug = raw.startsWith("field:") ? raw.slice(6) : raw;
  const g = catalog.labelGroups.find((x) => x.slug === slug);
  return g && canGroupBy(g) ? `field:${g.slug}` : fallback;
}

/** URL form of a grouping: field groupings use the bare slug (`?group=phase`). */
export const groupParam = (g: GroupBy) => (g.startsWith("field:") ? g.slice(6) : g);

const statusOrder = (catalog: Catalog, statusId: string) => {
  const s = catalog.statusById.get(statusId);
  return s ? STATUS_CATEGORIES.indexOf(s.category) * 1000 + s.position : 99_999;
};

/**
 * Builds the groups for `items` (already filtered and sorted). `includeEmpty` adds groups with no
 * items (board columns); `workflowIds` limits status columns to the workflows in scope.
 */
export function buildGroups(
  items: readonly ItemLite[],
  groupBy: GroupBy,
  catalog: Catalog,
  opts: { includeEmpty?: boolean; workflowIds?: string[]; meId?: string | null; productIds?: readonly string[] } = {},
): Group[] {
  const buckets = new Map<string, ItemLite[]>();
  const push = (key: string, item: ItemLite) => {
    const list = buckets.get(key);
    if (list) list.push(item);
    else buckets.set(key, [item]);
  };

  switch (groupBy) {
    case "none":
      return [{ key: "all", label: m.list_label(), items: [...items], patchFor: () => null }];

    case "status": {
      for (const i of items) push(i.statusId, i);
      const statuses = opts.includeEmpty
        ? (opts.workflowIds ?? []).flatMap((w) => catalog.statusesOf(w))
        : [...buckets.keys()].map((id) => catalog.statusById.get(id)).filter((s) => !!s);
      const ids = [...new Set([...statuses.map((s) => s.id), ...buckets.keys()])].sort(
        (a, b) => statusOrder(catalog, a) - statusOrder(catalog, b),
      );
      return ids.map((id) => {
        const s = catalog.statusById.get(id);
        return {
          key: id,
          label: s ? loc(s.name) : id,
          icon: s ? <StatusIcon category={s.category} decorative /> : undefined,
          accent: statusAccent(s?.color),
          items: buckets.get(id) ?? [],
          patchFor: (item) => {
            if (!s || item.statusId === id) return null;
            const wf = catalog.productById.get(item.productId)?.workflowId;
            if (wf && wf !== s.workflowId) {
              const list = catalog.statusesOf(wf);
              const mapped = list.find((x) => x.slug === s.slug) ?? list.find((x) => x.category === s.category);
              return mapped ? { statusId: mapped.id } : null;
            }
            return { statusId: id };
          },
        };
      });
    }

    case "priority": {
      for (const i of items) push(String(i.priority), i);
      const values = PRIORITY_VALUES.filter((v) => opts.includeEmpty || buckets.has(String(v)));
      return values.map((v) => ({
        key: String(v),
        label: priorityLabel(v),
        icon: <PriorityIcon value={v} />,
        items: buckets.get(String(v)) ?? [],
        patchFor: (item) => (item.priority === v ? null : { priority: v }),
      }));
    }

    case "type": {
      for (const i of items) push(i.typeId, i);
      const types = catalog.types.filter((t) => opts.includeEmpty || buckets.has(t.id));
      return types.map((t) => ({
        key: t.id,
        label: loc(t.name),
        icon: <TypeIcon name={t.icon} color={t.color} decorative />,
        items: buckets.get(t.id) ?? [],
        patchFor: (item) => (item.typeId === t.id ? null : { typeId: t.id }),
      }));
    }

    case "milestone": {
      const NONE = "__none";
      for (const i of items) push(i.milestoneId ?? NONE, i);
      const scope = new Set(opts.productIds ?? items.map((i) => i.productId));
      const milestones = catalog.milestones
        .filter((ms) => buckets.has(ms.id) || (opts.includeEmpty && scope.has(ms.productId) && ms.status === "open"))
        .sort(compareMilestones);
      const groups: Group[] = milestones.map((ms) => ({
        key: ms.id,
        label: milestoneLabel(ms),
        icon: (
          <FlagBanner
            size={14}
            weight={ms.status === "open" ? "regular" : "fill"}
            aria-hidden="true"
            className="text-fg-subtle"
          />
        ),
        items: buckets.get(ms.id) ?? [],
        patchFor: (item) =>
          item.milestoneId === ms.id || item.productId !== ms.productId ? null : { milestoneId: ms.id },
      }));
      if (buckets.has(NONE) || opts.includeEmpty)
        groups.push({
          key: NONE,
          label: m.prop_no_milestone(),
          items: buckets.get(NONE) ?? [],
          patchFor: (item) => (item.milestoneId ? { milestoneId: null } : null),
        });
      return groups;
    }

    case "product": {
      for (const i of items) push(i.productId, i);
      return catalog.allProducts
        .filter((p) => buckets.has(p.id))
        .map((p) => ({
          key: p.id,
          label: p.isSpaceProduct ? `${catalog.spaceById.get(p.spaceId)?.name ?? p.name}` : p.name,
          items: buckets.get(p.id) ?? [],
          patchFor: () => null,
        }));
    }

    case "assignee": {
      const NONE = "__none";
      for (const i of items) push(i.assigneeIds[0] ?? NONE, i);
      const users = [...catalog.users]
        .filter((u) => buckets.has(u.id) || (opts.includeEmpty && u.id === opts.meId))
        .sort((a, b) => Number(b.id === opts.meId) - Number(a.id === opts.meId) || a.name.localeCompare(b.name));
      const groups: Group[] = users.map((u) => ({
        key: u.id,
        label: u.name,
        icon: <Avatar name={u.name} seed={u.id} src={u.image} bot={u.kind === "bot"} size="xs" labelled={false} />,
        items: buckets.get(u.id) ?? [],
        patchFor: (item) =>
          item.assigneeIds.length === 1 && item.assigneeIds[0] === u.id
            ? null
            : { assignees: { add: [u.id], remove: item.assigneeIds.filter((x) => x !== u.id) } },
      }));
      if (buckets.has(NONE) || opts.includeEmpty)
        groups.push({
          key: NONE,
          label: m.unassigned(),
          items: buckets.get(NONE) ?? [],
          patchFor: (item) => (item.assigneeIds.length === 0 ? null : { assignees: { remove: item.assigneeIds } }),
        });
      return groups;
    }

    default: {
      const slug = groupBy.slice(6);
      const group = catalog.labelGroups.find((g) => g.slug === slug);
      if (!group) return [{ key: "all", label: m.list_label(), items: [...items], patchFor: () => null }];
      const labels = catalog.labels.filter((l) => l.groupId === group.id);
      const ids = new Set(labels.map((l) => l.id));
      const NONE = "__none";
      for (const i of items) push(i.labelIds.find((l) => ids.has(l)) ?? NONE, i);
      const groups: Group[] = labels
        .filter((l) => opts.includeEmpty || buckets.has(l.id))
        .map((l) => ({
          key: l.id,
          label: loc(l.name),
          icon: (
            <span
              aria-hidden="true"
              className="size-2 rounded-full"
              style={{ backgroundColor: labelSwatch(l.color) }}
            />
          ),
          items: buckets.get(l.id) ?? [],
          patchFor: (item) =>
            item.labelIds.includes(l.id)
              ? null
              : { labels: { add: [l.id], remove: item.labelIds.filter((x) => ids.has(x)) } },
        }));
      if (buckets.has(NONE) || opts.includeEmpty)
        groups.push({
          key: NONE,
          label: m.no_field_value({ field: loc(group.name) }),
          items: buckets.get(NONE) ?? [],
          patchFor: (item) => {
            const remove = item.labelIds.filter((x) => ids.has(x));
            return remove.length ? { labels: { remove } } : null;
          },
        });
      return groups;
    }
  }
}
